import React, { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { useCfoComplianceScore, useCfoScoreTrend } from '../../../hooks/analytics/useCfoReports.ts';
import { StatCard, WidgetCard, formatPercent } from '../controller/shared.tsx';
import { SEMANTIC } from '../theme.ts';

const BAND_COLORS: Record<string, string> = {
  healthy: SEMANTIC.good.chart,
  watch: SEMANTIC.warn.chart,
  critical: SEMANTIC.critical.chart,
};

const BAND_CHIP: Record<string, string> = {
  healthy: SEMANTIC.good.chipClass,
  watch: SEMANTIC.warn.chipClass,
  critical: SEMANTIC.critical.chipClass,
};

/**
 * Report #1 — SOX Compliance Score (Executive KPI). Semi-donut gauge (0-100)
 * with a band chip (Healthy/Watch/Critical), the three score components
 * (coverage / test pass rate / issue penalty), and a delta vs. the prior
 * score-trend snapshot when history exists.
 *
 * `useCfoComplianceScore` can resolve `not_computed: true` — the backend
 * computes the score on the fly (without persisting) when the nightly
 * refresh hasn't populated `report_compliance_score` yet, so the card
 * still renders a real number instead of a blank state.
 */
export const ComplianceScoreGauge: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCfoComplianceScore();
  const { data: trend } = useCfoScoreTrend();

  const row = data?.row;
  const bandKey = (row?.band ?? '').toLowerCase();
  const gaugeColor = BAND_COLORS[bandKey] ?? '#3b82f6';

  const gaugeData = useMemo(() => {
    const score = Math.max(0, Math.min(100, row?.score ?? 0));
    return [
      { name: 'score', value: score },
      { name: 'rest', value: 100 - score },
    ];
  }, [row?.score]);

  const delta = useMemo(() => {
    if (!trend || trend.length < 2) return null;
    const sorted = [...trend].sort(
      (a, b) => new Date(a.snapshot_date).getTime() - new Date(b.snapshot_date).getTime()
    );
    const last = sorted[sorted.length - 1];
    const prev = sorted[sorted.length - 2];
    return last.score - prev.score;
  }, [trend]);

  return (
    <WidgetCard
      title="SOX Compliance Score"
      description="Org-level composite: 40% coverage + 40% test pass rate − 20% issue penalty"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load compliance score.'}
      isEmpty={!isLoading && !isError && !row}
      emptyMessage="Compliance score has not been computed yet."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="relative h-48 w-48 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={gaugeData}
                dataKey="value"
                startAngle={210}
                endAngle={-30}
                innerRadius={62}
                outerRadius={82}
                stroke="none"
                isAnimationActive={false}
              >
                <Cell fill={gaugeColor} />
                <Cell fill="#e5e7eb" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-4xl font-bold text-gray-900">{row ? Math.round(row.score) : '—'}</span>
            <span className="text-xs text-gray-500">/ 100</span>
          </div>
        </div>

        <div className="flex-1 w-full">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {row && (
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold capitalize ring-1 ring-inset ${
                  BAND_CHIP[bandKey] ?? SEMANTIC.neutral.chipClass
                }`}
              >
                {row.band}
              </span>
            )}
            {data?.not_computed && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-500">
                Computed live — not yet persisted by the nightly refresh
              </span>
            )}
            {delta != null && (
              <span className={`text-xs font-medium ${delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)} pts vs. prior snapshot
              </span>
            )}
          </div>
          <div className="analytics-kpi-grid gap-3">
            <StatCard
              title="Coverage"
              value={formatPercent(row?.coverage_ratio != null ? row.coverage_ratio * 100 : null)}
              color="bg-blue-100 text-blue-600"
            />
            <StatCard
              title="Test Pass Rate"
              value={formatPercent(row?.test_pass_ratio != null ? row.test_pass_ratio * 100 : null)}
              color="bg-green-100 text-green-600"
            />
            <StatCard
              title="Issue Penalty"
              value={formatPercent(row?.issue_penalty != null ? row.issue_penalty * 100 : null)}
              color="bg-red-100 text-red-600"
            />
          </div>
          {row?.refreshed_at && (
            <p className="text-xs text-gray-400 mt-3">Last refreshed {new Date(row.refreshed_at).toLocaleString()}</p>
          )}
        </div>
      </div>
    </WidgetCard>
  );
};

export default ComplianceScoreGauge;
